const MAX_RETRIES = 3; // Limit the number of retries for storage full issues
let retryCount = 0; // Initialize retry counter
let initializedFormMetadata = {}; // Track initialized metadata for each form ID

/**
 * Safely set an item in sessionStorage and create a backup in localStorage.
 * Tracks the last access time using timestamps.
 * Handles cases where storage might be full.
 * @param {string} key - The key to store the item under.
 * @param {any} value - The value to store.
 * @param {boolean} [skipIfInitialized=false] - Skip storing if metadata has already been initialized.
 */
export const safeSetItem = (key, value, skipIfInitialized = false) => {
    try {
        // Skip storing if the form's metadata has already been initialized
        if (skipIfInitialized && initializedFormMetadata[key]) {
//            console.log(`[storageUtils] Skipping save for already initialized metadata: ${key}`);
            return;
        }

        const timestamp = Date.now();
        const item = { value, timestamp };

//        console.log(`[storageUtils] Preparing to set item: key = ${key}, value =`, value);

        sessionStorage.setItem(key, JSON.stringify(item));
        localStorage.setItem(key, JSON.stringify(item)); // Backup in localStorage

//        console.log(`[storageUtils] Successfully set item: ${key} in both sessionStorage and localStorage`);

        retryCount = 0; // Reset retry count on success

        // Mark this form's metadata as initialized
        initializedFormMetadata[key] = true;
    } catch (error) {
        if (error.name === 'QuotaExceededError' && retryCount < MAX_RETRIES) {
            retryCount++;
            console.warn(`[storageUtils] Storage full, clearing storage and retrying... (Attempt ${retryCount})`);
            handleStorageFull(); // Clear space and try again
            safeSetItem(key, value); // Retry saving
        } else {
            console.error(`[storageUtils] Error setting item ${key}:`, error);
            retryCount = 0; // Reset retry count on failure
        }
    }
};

/**
 * Safely get an item from sessionStorage, or fall back to localStorage.
 * Updates access timestamp.
 * @param {string} key - The key to retrieve the item from.
 * @param {boolean} [skipIfInitialized=false] - Skip fetching if metadata is already initialized.
 * @returns {any|null} The parsed value or null if not found or error occurs.
 */
export const safeGetItem = (key, skipIfInitialized = false) => {
    try {
//        console.log(`[safeGetItem] Checking for key: ${key}`);

        // Skip fetching if the metadata is already initialized
        if (skipIfInitialized && initializedFormMetadata[key]) {
//            console.log(`[safeGetItem] Skipping fetch for already initialized metadata: ${key}`);
            return null;
        }

        const sessionItem = sessionStorage.getItem(key);
        const localItem = localStorage.getItem(key);

//        console.log(`[safeGetItem] sessionItem for key ${key}:`, sessionItem);
//        console.log(`[safeGetItem] localItem for key ${key}:`, localItem);

        const storageItem = sessionItem || localItem;
        if (storageItem) {
            const parsedItem = JSON.parse(storageItem);
            const { value, timestamp } = parsedItem;

//            console.log(`[safeGetItem] Retrieved item for key: ${key}, value:`, value);

            // Only update the timestamp if more than 1 hour has passed since the last access
            if (Date.now() - timestamp > 3600000) {
                const updatedItem = { value, timestamp: Date.now() };
                sessionStorage.setItem(key, JSON.stringify(updatedItem));
                localStorage.setItem(key, JSON.stringify(updatedItem));
            }

            // Mark this form's metadata as initialized
            initializedFormMetadata[key] = true;

            return value; // Return the uncompressed value
        }

        console.warn(`[safeGetItem] Item ${key} not found in sessionStorage or localStorage.`);
        return null;
    } catch (error) {
        console.error(`[safeGetItem] Error getting item ${key}:`, error);
        return null;
    }
};

/**
 * Safely remove all field-related data (metadata, styles, dependencies).
 * @param {string} formId - The ID of the form.
 * @param {string} fieldId - The ID of the field to remove.
 */
export const safeRemoveFieldData = (formId, fieldId) => {
    try {
//        console.log(`[storageUtils] 🔄 Removing all data for field ID: ${fieldId} from form ID: ${formId}`);

        // Remove field metadata
//        console.log(`[storageUtils] Removing field metadata for field ID: ${fieldId}`);
        safeRemoveItem(`field_${fieldId}_metadata`);

        // Remove field styling/theme
//            console.log(`[storageUtils] Removing field theme for field ID: ${fieldId}`);
        safeRemoveItem(`field_${fieldId}_theme`);

        // Remove field dependencies
//        console.log(`[storageUtils] Removing field dependencies for field ID: ${fieldId}`);
        safeRemoveItem(`field_${fieldId}_dependencies`);

        // Remove stored values
//        console.log(`[storageUtils] Removing stored data for field ID: ${fieldId}`);
        safeRemoveItem(`field_${fieldId}_data`);

        // Remove field from layoutConfig
        const layoutKey = `form_${formId}_layoutConfig`;
        let layoutConfig = safeGetItem(layoutKey);

        if (layoutConfig) {
//            console.log(`[storageUtils] Filtering out field ID: ${fieldId} from layoutConfig`);
            Object.keys(layoutConfig).forEach(rowKey => {
                layoutConfig[rowKey] = layoutConfig[rowKey].filter(field => field.id !== fieldId);
            });

//            console.log(`[storageUtils] 🛠 Updated layoutConfig after field removal:`, layoutConfig);
            safeSetItem(layoutKey, layoutConfig); // Save updated layout
        }

//        console.log(`[storageUtils] ✅ Successfully removed all data for field ID: ${fieldId}`);
    } catch (error) {
        console.error(`[storageUtils] ❌ Error removing field data for field ID: ${fieldId}:`, error);
    }
};

/**
 * Safely remove an item from sessionStorage and localStorage.
 * @param {string} key - The key to remove the item from.
 */
export const safeRemoveItem = (key) => {
    try {
//        console.log(`[storageUtils] 🗑️ Removing item: ${key} from storage`);

        sessionStorage.removeItem(key);
        localStorage.removeItem(key); // Remove backup
        delete initializedFormMetadata[key];

//        console.log(`[storageUtils] ✅ Successfully removed item: ${key}`);
    } catch (error) {
        console.error(`[storageUtils] ❌ Error removing item ${key}:`, error);
    }
};

/**
 * Clear all data from sessionStorage and localStorage.
 */
export const clearStorage = () => {
    try {
        sessionStorage.clear();
        localStorage.clear();
        initializedFormMetadata = {}; // Reset the initialized metadata tracking
//        console.log(`[storageUtils] Cleared sessionStorage and localStorage.`);
    } catch (error) {
        console.error(`[storageUtils] Error clearing storage:`, error);
    }
};

/**
 * Handle storage full situation by clearing the oldest or least recently used data.
 * This ensures that more recent and important data is preserved.
 */
const handleStorageFull = () => {
    try {
        console.warn('[storageUtils] Handling storage full: selectively clearing older data...');

        const itemsToClear = 5; // Number of items to clear at a time
        const sessionItems = getAllItemsWithTimestamps(sessionStorage);
        const localItems = getAllItemsWithTimestamps(localStorage);

        // Combine session and local items, sort by timestamp (oldest first)
        const allItems = [...sessionItems, ...localItems].sort((a, b) => a.timestamp - b.timestamp);

        if (allItems.length > 0) {
            const itemsToDelete = allItems.slice(0, itemsToClear); // Select the oldest items

            itemsToDelete.forEach(item => {
//                console.log(`[storageUtils] Removing item with key: ${item.key}`);
                sessionStorage.removeItem(item.key);
                localStorage.removeItem(item.key);
            });

//            console.log(`[storageUtils] Cleared ${itemsToDelete.length} oldest items from sessionStorage and localStorage.`);
        } else {
            console.warn('[storageUtils] No data to clear or insufficient data.');
        }
    } catch (error) {
        console.error('[storageUtils] Error handling storage full:', error);
    }
};

/**
 * Helper function to retrieve all items in storage with their respective timestamps.
 * @param {Storage} storage - The storage (sessionStorage or localStorage) to scan.
 * @returns {Array} An array of objects containing the key and timestamp of each item.
 */
const getAllItemsWithTimestamps = (storage) => {
    const items = [];
    try {
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            const item = JSON.parse(storage.getItem(key));

            if (item && item.timestamp) {
                items.push({ key, timestamp: item.timestamp });
            }
        }
    } catch (error) {
        console.error('[storageUtils] Error retrieving items with timestamps:', error);
    }
    return items;
};
